import Modal from "../ui/Modal";
import { useState } from "react";
import { Archive, RotateCcw, Trash2 } from "lucide-react";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title?: string;
  message?: string;
  action?: "delete" | "archive" | "restore";
  confirmLabel?: string;
  loadingLabel?: string;
  cancelLabel?: string;
  errorMessage?: string;
};

export default function ConfirmDeleteModal({
  open,
  onClose,
  onConfirm,
  title = "Подтверждение удаления",
  message = "Вы уверены, что хотите удалить?",
  action = "delete",
  confirmLabel = "Удалить",
  loadingLabel = "Удаляем…",
  cancelLabel = "Отмена",
  errorMessage = "Ошибка при удалении",
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleConfirm() {
    setLoading(true);
    setError("");
    try {
      await onConfirm();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || e?.response?.data?.message || e?.message || errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const ActionIcon = action === "archive" ? Archive : action === "restore" ? RotateCcw : Trash2;

  return (
    <Modal open={open} onClose={onClose} title="" widthClass="max-w-md">
      <div className="space-y-6 text-center">
        <div
          className={[
            "mx-auto flex h-14 w-14 items-center justify-center rounded-2xl shadow-inner",
            action === "archive"
              ? "bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700"
              : action === "restore"
                ? "bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-700"
                : "bg-gradient-to-br from-rose-100 to-amber-100 text-rose-600",
          ].join(" ")}
        >
          <ActionIcon size={24} />
        </div>
        <div>
          <h3 className="text-2xl font-semibold text-slate-900">{title}</h3>
          <p className="mt-2 text-sm text-slate-600 break-words">{message}</p>
        </div>

        {error && (
          <div className="animate-softFade rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800 shadow-inner">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-center">
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={[
              "inline-flex flex-1 items-center justify-center rounded-2xl px-6 py-3 text-sm font-semibold text-white transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50 sm:flex-none",
              action === "archive"
                ? "bg-gradient-to-r from-amber-500 to-orange-500 shadow-[0_18px_30px_rgba(245,158,11,0.3)]"
                : action === "restore"
                  ? "bg-gradient-to-r from-emerald-500 to-teal-500 shadow-[0_18px_30px_rgba(16,185,129,0.3)]"
                  : "bg-gradient-to-r from-rose-500 to-rose-600 shadow-[0_18px_30px_rgba(225,29,72,0.35)]",
            ].join(" ")}
          >
            {loading ? loadingLabel : confirmLabel}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 sm:flex-none"
            disabled={loading}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
